import { useEffect, useState } from "react";

export type TruncatedLayoutState =
  | "user-expanded" // user manually expanded the card
  | "auto-truncated" // card was truncated because it was too long
  | "no-truncation-needed"; // content is short enough to not need truncation

export function useTruncatedLayout(maxHeight: number = 144) {
  const [toggleExpanded, setToggleExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );

  useEffect(() => {
    if (!contentElement) {
      return;
    }
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { height } = entry.contentRect;
        setContentHeight(height);
      }
    });
    resizeObserver.observe(contentElement);
    return () => {
      resizeObserver.unobserve(contentElement);
    };
  }, [contentElement]);

  return {
    truncationState: getTruncationState(
      contentHeight,
      maxHeight,
      toggleExpanded,
    ),
    setToggleExpanded,
    contentRef: setContentElement,
  };
}

function getTruncationState(
  contentHeight: number,
  maxHeight: number,
  toggleExpanded: boolean,
): TruncatedLayoutState {
  if (contentHeight <= maxHeight) {
    return "no-truncation-needed";
  }
  if (toggleExpanded) {
    return "user-expanded";
  }
  return "auto-truncated";
}
